import { type GroupResponse } from '@/types/GroupResponse';
import { useData } from '@/hooks/useData';
import { useEffect, useState } from 'react';
import { useToast } from '@/hooks/useToast';
import { createGroup, deleteGroup } from '@/utils/api';
import CreateGroupSection from '../settings/account/sections/CreateGroupSection';
import DeleteGroupSection from '../settings/account/sections/DeleteGroupSection';
import SectionTitle from '../settings/admin/SectionTitle';
import { Roles } from '@/types/Roles';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Toaster, toast } from 'sonner';

export default function GroupModal({
  groupOptions,
  refreshGroups,
}: {
  groupOptions: GroupResponse[];
  refreshGroups: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [group, setGroup] = useState<string[]>([]);
  const [isNewPrivate, setIsNewPrivate] = useState<boolean>(true);
  const { profile, displayedLocations, selectedFranchise, cachedGroups, setCachedGroups } = useData();
  const [currentSelectedGroup, setCurrentSelectedGroup] = useState<GroupResponse[]>(cachedGroups);
  const [creating, setCreating] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  const handleGroupChange = (locationId: string) => {
    setGroup((prev) => {
      if (prev.includes(locationId)) {
        return prev.filter((loc) => loc !== locationId);
      } else {
        return [...prev, locationId];
      }
    });
  };

  useEffect(() => {
    setCurrentSelectedGroup(cachedGroups);
  }, [cachedGroups]);


  // Handle creating new group.
  async function handleNewGroup(name: string, group: string[]): Promise<void> {
    if (!selectedFranchise) return;
    setCreating(true);

    await createGroup(name, selectedFranchise.id, group, isNewPrivate).then((res) => {
      toast.success(`Group created ${name} successfully`);
      return res;
    }).catch(() => {
      toast.error(`Error creating ${name} group`);
      return false;
    }).finally(() => {
      setCreating(false);
    });

    refreshGroups();
  }

  // Handle Deleting a group.
  async function handleDeleteGroup(name: string): Promise<void> {
    if (!selectedFranchise) return;
    const isPrivate = groupOptions.find(g => g.groupId == name)?.private ?? true;
    setDeleting(true);
    await deleteGroup(name, selectedFranchise.id, isPrivate).then((res) => {
      toast.success(`Group ${name} deleted successfully`);
      return res;
    }).catch(() => {
      toast.error(`Error deleting ${name} group`);
      return false;
    }).finally(() => {
      setDeleting(false);
    });
    refreshGroups();
  }

  const handleSelection = (group: GroupResponse) => {
    if (currentSelectedGroup.some((g) => g.groupId === group.groupId)) {
      setCurrentSelectedGroup(currentSelectedGroup.filter((g) => g.groupId !== group.groupId));
    } else {
      setCurrentSelectedGroup([...currentSelectedGroup, group]);
    }
  };

  const handleSetSelectedGroups = () => {
    setCachedGroups(currentSelectedGroup);
  };


  const handleRemove = () => {
    setCurrentSelectedGroup([]);
  };

  const handlePrivateChange = (isPrivate: boolean) => {
    setIsNewPrivate(isPrivate);
  };

  let isAdmin = false;
  if (selectedFranchise) {
    isAdmin = profile?.franchise.userRole !== undefined && profile.franchise.userRole.includes(Roles.ADMIN);
  }

  const liClassName = 'cursor-pointer w-4 h-4 text-blue-600 bg-slate-100 border-slate-300 rounded-sm \
    focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-slate-700 dark:focus:ring-offset-slate-700 \
    focus:ring-2 dark:bg-slate-600 dark:border-slate-500';

  if (groupOptions.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="flex flex-col h-[74vh]" style={{ overflowY: 'scroll' }}>
      <Toaster />
      <div className={'m-1 p-2 border border-slate-100 dark:border-slate-700 rounded-md'}>
        <SectionTitle title="Select a Group" />
        <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm">
          Select a group of locations to see data for.
        </p>
        <ul className={'text-sm font-medium text-slate-900 bg-white border border-slate-200 rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white'}>
          {groupOptions?.map((group, i) => (
            <li key={`${group.groupId}-${i}`} className={'w-full'}>
              <div
                className={'flex items-center ps-3'}
              >
                <input
                  checked={currentSelectedGroup.some((g) => g.groupId === group.groupId)}
                  id={`list-radio-${group.groupId}-checkbox`}
                  type="checkbox"
                  value=""
                  name="list-checkbox"
                  onChange={() => { handleSelection(group); }}
                  className={liClassName}
                />
                <label htmlFor={`list-radio-${group.groupId}-checkbox`} className={'w-full py-2 ms-2 text-sm font-medium text-slate-900 dark:text-slate-300'}>
                  {(group.displayName.length > 0) ? group.displayName : group.groupId}
                </label>
              </div>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-3 gap-4">
          <div
            className={`p-2 my-1 cursor-pointer font-medium shadow-sm
            text-sm text-indigo-500 dark:text-white flex justify-center
            bg-white dark:bg-slate-700
            border border-slate-200 hover:border-slate-300 rounded-lg dark:border-slate-700 dark:hover:border-slate-600`}
            onClick={() => { handleSetSelectedGroups(); }}>
            Confirm Selection
          </div>
          <div
            className={`p-2 my-1 cursor-pointer font-medium shadow-sm
            text-sm text-indigo-500 dark:text-white flex justify-center
            bg-white dark:bg-slate-700
            border border-slate-200 hover:border-slate-300 rounded-lg dark:border-slate-700 dark:hover:border-slate-600`}
            onClick={() => { handleRemove(); }}>
            Remove Selection
          </div>

        </div>
      </div>

      {/* Create Group */}
      {selectedFranchise && (
        <div className={'m-1 p-2 border border-slate-100 dark:border-slate-700 rounded-md'}>
          <CreateGroupSection
            locations={displayedLocations.filter(
              (loc) => loc.franchiseId === selectedFranchise.id
            )}
            disabled={creating}
            franchise={selectedFranchise}
            name={name}
            setName={setName}
            group={group}
            isAdmin={isAdmin}
            handlePrivateChange={handlePrivateChange}
            handleChange={handleGroupChange}
            handleCreate={(name, group) => {
              handleNewGroup(name, group).catch(console.error);
            }}

          />
        </div>
      )}

      {/* Delete Group */}
      {selectedFranchise && (
        <div className={'m-1 p-2 border border-slate-100 dark:border-slate-700 rounded-md'}>
          <DeleteGroupSection
            franchise={selectedFranchise}
            groups={groupOptions}
            disabled={deleting}
            handleDelete={(name) => {
              handleDeleteGroup(name).catch(console.error);
            }}

          />
        </div>
      )}
    </div>
  );
}
